import { sumBy, debounce } from "lodash-es";
import { Controller } from "stimulus";

import useEventController from "../../composables/use_event_controller";
import useStatusClasses from "../../composables/use_status_classes";

export default class extends Controller {
  static targets = [
    "cardCheckbox",
    "headerAllocationCount",
    "headerIssues",
    "headerIssuesCount",
    "headerIssuesIcon",
    "headerResolvedIcon",
    "rowCheckbox",
    "rowCheckmark",
    "progressBar",
    "progressOfAllocations",
    "progressOfIssues",
    "receivableInput",
  ];

  static values = {
    status: { type: String, default: "need_to_receive" },
    allocation: { type: Number, default: 0 },
    initialAllocation: { type: Number, default: 0 },
    maxAllocation: { type: Number, default: 0 },
  };

  static classes = ["issuesFlag", "resolvedFlag"];

  updateCard = debounce(this.#updateCard.bind(this), 10);
  totalIssues = 0;
  goodsControllers = [];

  connect() {
    useEventController(this);
    useStatusClasses(this, "card");

    this.listenOn(document, "receivingReset", this.reset.bind(this));
    this.listenOn(document, "receivingRefresh", this.refresh.bind(this));
    this.listenOn(this.element, "focusin", this.#buildControllers.bind(this));

    // start listening for changes AFTER connecting to avoid unwanted initial value change calls
    Object.assign(this, {
      allocationValueChanged: this.updateCardState.bind(this),
      initialAllocationValueChanged: this.#initialAllocationValueUpdated.bind(this),
      statusValueChanged: this.#statusValueUpdated.bind(this),
    });

    this.statusValueChanged(this.statusValue, undefined);

    setTimeout(() => {
      this.updateCounts();
      this.updateCheckboxVisibility();
    });
  }

  flaggingModeUpdated({ detail }) {
    this.element.classList.toggle("crosshair", detail.enabled);

    this.cardCheckboxTarget.disabled = !!detail.type;
    this.rowCheckboxTargets.forEach((checkbox) => {
      checkbox.disabled = this.cardCheckboxTarget.disabled;
    });
  }

  refresh() {
    this.initialAllocationValue = this.allocationValue;
    this.updateCardState();
    this.resetCheckboxes();
    this.updateCheckboxVisibility();
  }

  reset() {
    this.allocationValue = this.initialAllocationValue;
    this.resetCheckboxes();
  }

  resetCheckboxes() {
    this.cardCheckboxTarget.indeterminate = false;
    this.cardCheckboxTarget.checked = false;

    this.rowCheckboxTargets.forEach((checkbox) => {
      checkbox.indeterminate = false;
      checkbox.checked = false;
    });
  }

  syncCheckboxStates() {
    this.element.querySelectorAll("[data-component=groupRow]").forEach((row) => {
      const receivableCheckboxes = [...row.querySelectorAll("[data-component=receivableGood]")]
        .filter((receivable) => receivable.dataset.status !== "received" && receivable.dataset.control === "main")
        .flatMap((receivable) => receivable.querySelector("[data-component=goodsCheckbox]"));

      // If any of the controls on a row are checked, check the row itself.
      const rowCheckbox = row.querySelector("[data-component=groupRowCheckbox]");
      rowCheckbox.checked = receivableCheckboxes.some((checkbox) => checkbox.checked);

      // If we've checked the row, but there are unchecked or indeterminate controls,
      // then mark the row as indeterminate as well.
      rowCheckbox.indeterminate =
        rowCheckbox.checked && receivableCheckboxes.some((checkbox) => !checkbox.checked || checkbox.indeterminate);
    });

    this.cardCheckboxTarget.checked = this.rowCheckboxTargets.some((checkbox) => checkbox.checked);

    this.cardCheckboxTarget.indeterminate =
      this.cardCheckboxTarget.checked &&
      this.rowCheckboxTargets.some((checkbox) => checkbox.checked && checkbox.indeterminate);
  }

  toggleCardCheckbox(event) {
    // This action can take a second on larger PO's, so prevent user from trying again right away.
    this.element.classList.add("pointer-events-none");

    this.element.querySelectorAll("[data-component='groupRowCheckbox']").forEach((checkbox) => {
      checkbox.indeterminate = false;
      checkbox.checked = event.target.checked;
      checkbox.dispatchEvent(new Event("change"));
    });

    setTimeout(() => this.element.classList.remove("pointer-events-none"));
  }

  toggleRowCheckbox(event) {
    const row = event.target.closest("[data-component=groupRow]");
    this.dispatch(`receivingRowToggleRow:${row.dataset.domId}`, {
      prefix: false,
      detail: { checked: event.target.checked },
    });
  }

  updateProgressBar() {
    this.headerAllocationCountTarget.textContent = this.allocationValue;

    if (this.hasHeaderIssuesTarget) {
      const allIssuesResolved = this.issuesResolved;
      this.headerIssuesCountTarget.textContent = allIssuesResolved ? this.totalIssues : this.outstandingIssues;
      this.headerIssuesTarget.classList.toggle("hidden", !this.totalIssues);
      this.headerIssuesIconTarget.classList.toggle("hidden", allIssuesResolved);
      this.headerResolvedIconTarget.classList.toggle("hidden", !allIssuesResolved);

      this.headerIssuesTarget.classList.toggle(this.resolvedFlagClass, allIssuesResolved);
      this.headerIssuesTarget.classList.toggle(this.issuesFlagClass, !allIssuesResolved);

      this.progressOfIssuesTarget.style.width = `${this.issuePercentage}%`;
    }

    this.progressOfAllocationsTarget.style.width = `${this.selectedPercentage}%`;
    this.progressBarTarget.dataset.indicates = this.indicatorStatus;
  }

  updateCardState() {
    if (this.fullyAllocated && this.issuesResolved) {
      this.statusValue = "received";
    } else if (this.allocationValue > 0) {
      this.statusValue = "partially_received";
    } else {
      this.statusValue = "need_to_receive";
    }
  }

  updateCheckboxVisibility() {
    this.updateCardCheckboxVisibility();
    this.updateRowCheckboxVisibility();
  }

  updateCardCheckboxVisibility() {
    const cardHasChanges = this.allGoodsControllers.some((controller) => controller.isDirty);
    const shouldShowCheckmark = this.fullyAllocated && this.issuesResolved && !cardHasChanges;

    if (this.hasCardCheckboxTarget) {
      this.cardCheckboxTarget.classList.toggle("hidden", shouldShowCheckmark);
    }
  }

  updateRowCheckboxVisibility() {
    this.rowCheckboxTargets.forEach((rowCheckbox) => {
      const row = rowCheckbox.closest("[data-component=groupRow]");

      const rowGoods = [...row.querySelectorAll("[data-controller=goods--receivable-good]:not([data-control=main])")]
        .map((element) => this.application.getControllerForElementAndIdentifier(element, "goods--receivable-good"))
        .filter(Boolean);

      const rowFullyAllocated = rowGoods.every((controller) => controller.fullyAllocated && controller.issuesResolved);
      const rowHasChanges = rowGoods.some((controller) => controller.isDirty);
      const shouldShowCheckmark = rowFullyAllocated && !rowHasChanges;

      rowCheckbox.classList.toggle("hidden", shouldShowCheckmark);
      row
        .querySelector("[data-goods--receivable-card-target=rowCheckmark]")
        ?.classList.toggle("hidden", !shouldShowCheckmark);
    });
  }

  updateCounts() {
    this.totalIssues = this.receivableInputTargets.reduce(
      (total, input) => total + (parseInt(input.dataset.issues, 10) || 0),
      0,
    );

    this.allocationValue = Math.max(
      0,
      this.receivableInputTargets.reduce((total, input) => total + parseInt(input.value, 10), 0),
    );

    setTimeout(() => this.updateProgressBar());
  }

  #updateCard() {
    setTimeout(() => {
      this.updateCounts();
      this.updateCardState();
      this.syncCheckboxStates();
      this.updateCheckboxVisibility();
    });
  }

  get fullyAllocated() {
    return this.allGoodsControllers.every((controller) => controller.fullyAllocated);
  }

  get issuesResolved() {
    return this.allGoodsControllers.every((controller) => controller.issuesResolved);
  }

  get allGoodsControllers() {
    const elements = [...this.element.querySelectorAll("[data-component=receivableGood]:not([data-control=main])")];

    if (this.goodsControllers.length < elements.length) {
      this.goodsControllers = elements
        .map((element) => this.application.getControllerForElementAndIdentifier(element, "goods--receivable-good"))
        .filter(Boolean);
    }

    return this.goodsControllers;
  }

  get selectedPercentage() {
    return Math.min(100, (this.allocationValue / this.maxAllocationValue) * 100);
  }

  get issuePercentage() {
    return Math.min(100, (this.outstandingIssues / this.maxAllocationValue) * 100);
  }

  get indicatorStatus() {
    if (!this.cardCheckboxTarget.indeterminate && this.selectedPercentage >= 100 && this.issuesResolved) {
      return "full";
    }

    if (this.selectedPercentage) {
      return "partial";
    }

    return "empty";
  }

  get outstandingIssues() {
    return this.totalIssues - sumBy(this.allGoodsControllers, "resolvedCount");
  }

  #initialAllocationValueUpdated() {
    this.updateCardState();
  }

  #statusValueUpdated(value, previousValue) {
    this.element.dataset.status = value;
    this.updateStatusClasses(value, previousValue);
  }

  #buildControllers() {
    const missing = Array.from(this.element.querySelectorAll("[data-controller-delay]"));

    missing.forEach((element) => {
      const { controllerDelay, controller } = element.dataset;

      if (controller !== controllerDelay) {
        element.dataset.controller = controllerDelay;
      }
    });
  }
}
